<template>
	<div>
		<label v-if="label" class="block input-label" v-text="label" :class="[validationError? 'text-error-content' : '']"></label>
		<VueDatePicker
			:key="datePickerRenderKey"
			v-bind="$attrs"
			:locale="dateFnsLocale || undefined"
			:action-row="{showSelect: false, showCancel: false}"
			:input-attrs="{ clearable }"
			auto-apply
			:time-config="{ enableTimePicker: false }"
			position="left"
			teleport="body"
			:formats="{ input: datePickerDateFormat }"
		>
			<template v-if="asInput" #dp-input="{ value, onInput, onChange, onBlur, onFocus, onKeydown }">
				<label class="input flex items-center gap-2" :class="inputCssClassesIncludingErrorState">
					<CalendarDaysIcon class="shrink-0 w-5 h-5 opacity-60" />
					<input
						type="text"
						class="grow bg-transparent"
						:value="value"
						:placeholder="placeholder"
						@input="onInput"
						@change="onChange"
						@blur="onBlur"
						@focus="onFocus"
						@keydown="onKeydown"
					/>
				</label>
			</template>
			<template v-else #trigger>
				<button type="button" class="btn btn-ghost bg-base-100 btn-sm">
					<CalendarDaysIcon class="w-5 h-5" />
				</button>
			</template>
			<template #preset-date-button="{ label, value, presetDate }">
				<button type="button" class="btn btn-sm btn-ghost normal-case whitespace-nowrap justify-start hover:bg-accent"
					:tabindex="0"
					@click="presetDate(value)"
					@keyup.enter.prevent="presetDate(value)"
				>
					{{ label }}
				</button>
			</template>

			</VueDatePicker>
	</div>
	<span v-if="validationError" class="text-red-600 text-xs">
		{{ validationError }}
	</span>
</template>

<style>
.dp__outer_menu_wrap {
	@apply shadow-md;
}
</style>

<script lang="ts">
import { VueDatePicker } from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { CalendarDaysIcon } from '@heroicons/vue/24/outline';
import type { Locale } from 'date-fns';
import { getDateFnsLocale } from '@/i18n.ts';

export default {
	inheritAttrs: false,

  	components: {
		VueDatePicker,
		CalendarDaysIcon,
  	},

	props: {
		label: {
			type: String,
		},
		asInput: {
			type: Boolean,
			default: true,
		},
		inputCssClasses: {
			type: String,
			default: '',
		},
		placeholder: {
			type: String,
			default: '',
		},
		validationError: {
			type: String
		},
		clearable: {
			type: Boolean,
			default: true,
		},
	},

	data() {
		return {
			dateFnsLocale: null as Locale | null,
			datePickerRenderKey: 0,
		};
	},

	computed: {

        datePickerDateFormat() {
            const dateFormatOptions: Intl.DateTimeFormatOptions = {
                year: "2-digit",
                month: "short",
                day: "2-digit"
            };

            // date-fns Locale objects expose a BCP-47 `code` (e.g. "nl-BE").
			return (date: Date) => {
				const localeCode = this.dateFnsLocale?.code || 'en-GB'; // To prevent mounting errors, initialise with 'en-GB' if the date-fns locale isn't loaded yet
				return date.toLocaleDateString(localeCode, dateFormatOptions);
			};
        },

		inputCssClassesIncludingErrorState() {
			let classes = this.inputCssClasses;
			if(this.validationError) {
				classes += ' input-error';
			}
			return classes;
		},
	},

	methods: {
		async loadDateFnsLocale() {
			this.dateFnsLocale = await getDateFnsLocale(this.$i18n.locale);
			// Targeted remount strategy: when locale finishes loading, the picker gets a new key so it rebuilds with the correct
			// formatter/locale instead of keeping initial cached (default) formatting.
			this.datePickerRenderKey += 1;
		},
	},

	async mounted() {
		await this.loadDateFnsLocale();
	},
};
</script>
